/**
 * Comments API functions - TypeScript version
 * Authentication headers are handled automatically by the axios instance
 */

import { AxiosInstance, AxiosResponse } from "axios"
import { Comment, CommentReply, CommentsStorageData } from "types/comments.types"

/**
 * Fetches all comments for a project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project (hint_owner)
 * @returns Promise resolving to comments data
 * @throws Will throw an error if the request fails
 */
export async function getComments(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<AxiosResponse<CommentsStorageData>> {
  const response = await axiosInstance.get<CommentsStorageData>(
    `/comments-get?id=${id}&owner=${owner}`
  )
  return response
}

/**
 * Saves all comments for a project (bulk save).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param data - The full comments storage data
 * @returns Promise resolving to save confirmation
 * @throws Will throw an error if the request fails
 */
export async function saveComments(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  data: CommentsStorageData
): Promise<AxiosResponse<{ success: boolean }>> {
  const response = await axiosInstance.post(
    `/comments-save?id=${id}&owner=${owner}`,
    data
  )
  return response
}

/**
 * Adds a new comment to a project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param comment - The comment to add
 * @returns Promise resolving to the created comment ID
 * @throws Will throw an error if the request fails
 */
export async function addComment(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  comment: Comment
): Promise<AxiosResponse<{ id: string }>> {
  const response = await axiosInstance.post(
    `/comment-add?id=${id}&owner=${owner}`,
    comment
  )
  return response
}

/**
 * Updates an existing comment.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param commentId - The ID of the comment to update
 * @param updates - Partial comment data to update
 * @returns Promise resolving to success status
 * @throws Will throw an error if the request fails
 */
export async function updateCommentApi(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  commentId: string,
  updates: Partial<Comment>
): Promise<AxiosResponse<{ success: boolean }>> {
  const response = await axiosInstance.post(
    `/comment-update?id=${id}&owner=${owner}&comment_id=${commentId}`,
    updates
  )
  return response
}

/**
 * Deletes a comment (soft delete).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param commentId - The ID of the comment to delete
 * @param deletionData - Deletion metadata (who deleted, reason)
 * @returns Promise resolving to success status
 * @throws Will throw an error if the request fails
 */
export async function deleteCommentApi(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  commentId: string,
  deletionData: {
    deletedBy: string
    deletedByName: string
    reason: string
  }
): Promise<AxiosResponse<{ success: boolean }>> {
  const response = await axiosInstance.post(
    `/comment-delete?id=${id}&owner=${owner}&comment_id=${commentId}`,
    deletionData
  )
  return response
}

/**
 * Resolves a comment.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param commentId - The ID of the comment to resolve
 * @param resolvedBy - User ID who resolved the comment
 * @param resolvedByName - User name who resolved the comment
 * @returns Promise resolving to success status
 * @throws Will throw an error if the request fails
 */
export async function resolveCommentApi(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  commentId: string,
  resolvedBy: string,
  resolvedByName: string
): Promise<AxiosResponse<{ success: boolean }>> {
  const response = await axiosInstance.post(
    `/comment-resolve?id=${id}&owner=${owner}&comment_id=${commentId}`,
    { resolvedBy, resolvedByName }
  )
  return response
}

/**
 * Adds a reply to a comment.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param commentId - The ID of the comment to reply to
 * @param reply - The reply to add
 * @returns Promise resolving to the created reply ID
 * @throws Will throw an error if the request fails
 */
export async function addReplyApi(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  commentId: string,
  reply: CommentReply
): Promise<AxiosResponse<{ id: string }>> {
  const response = await axiosInstance.post(
    `/reply-add?id=${id}&owner=${owner}&comment_id=${commentId}`,
    reply
  )
  return response
}

/**
 * Deletes a reply from a comment.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project ID
 * @param owner - The owner of the project
 * @param commentId - The ID of the comment containing the reply
 * @param replyId - The ID of the reply to delete
 * @returns Promise resolving to success status
 * @throws Will throw an error if the request fails
 */
export async function deleteReplyApi(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  commentId: string,
  replyId: string
): Promise<AxiosResponse<{ success: boolean }>> {
  const response = await axiosInstance.post(
    `/reply-delete?id=${id}&owner=${owner}&comment_id=${commentId}&reply_id=${replyId}`,
    {}
  )
  return response
}
